/**
 * +specs: the Mage's three specialisations (docs/design/classes/specs.md "Mage"). Built on the
 * Warrior reference (warrior.ts): every base key 1–7 evolves on the same key, the signature mechanic
 * lives in passives / auras, spec-only abilities sit on Q.
 *
 *  - Stormcaller (ranged dps, overvolt lightning): Voltage 0–130. Above 100 you Overvolt (+damage,
 *    you burn 1% health a second); Skybreak vents it all as chain lightning (S11 spend-all: bigger
 *    with every point, one more jump per 25).
 *  - Rimecaller (ranged dps, freeze and fracture): Rime Tithe banks Frost Spines (pips, 0–5);
 *    Hoarlance launches them all (S11). Hoarlance always crits a Frozen target (Fracture); Hoarbite
 *    procs and a full 5-Spine volley count as frozen. Whiteout, Rime Ring, Frostfold, the Wandering
 *    Glacier and Deep Winter freeze.
 *  - Cinderborn (ranged dps, burns its own life): fire costs health, never mana. Pyre turns missing
 *    health into damage and lifesteal; kills feed you; once a fight a killing blow makes you rise
 *    from the ash as a phoenix (Ashrise, S9 cheat death).
 *
 * Every spec evolves the fan-favourite Wandering Star (key 7) into a bigger drifting orb with a
 * spec twist (Voltage per zap, a freezing glacier, a healing sun).
 *
 * Names: original (checked against WoW's spell list; names.ts holds the denylist incl. the ones avoided
 * here, e.g. the resource is Voltage because "Charge" is a WoW spell).
 * Numbers: tuned with `pnpm harness -- specs` (docs/design/classes/specs.md §9).
 */
import type { AbilityDef, AuraDef, Condition, EffectSpec, SpecDef } from '@keystone/sim';
import { ABILITY, BASE_ABILITY } from '../../ids';
import { OFF_GCD, SPELL_RANGE, aroundSelf, aroundTarget } from '../common';
import { type SpecInfo, infoOf } from './types';

const MA = BASE_ABILITY.mage;
const S = ABILITY.stormcaller;

export const MAGE_SPEC = { stormcaller: 'stormcaller', rimecaller: 'rimecaller', cinderborn: 'cinderborn' } as const;

/** Ability ids (key in the comment). Checked against the legacy `stormcaller.*` kit: no collisions. */
export const STORMCALLER = {
  spark: 'stormcaller.spark', // 1 (Mana Dart)
  skybreak: 'stormcaller.skybreak', // 2 (Arcane Lance)
  staticCage: 'stormcaller.static_cage', // 3 (Hoarbind)
  staticStep: 'stormcaller.static_step', // 4 (Fold Step)
  chainStorm: 'stormcaller.chain_storm', // 5 (Flare Burst)
  arcWard: 'stormcaller.arc_ward', // 6 (Glyph Shield)
  wanderingTempest: 'stormcaller.wandering_tempest', // 7 (Wandering Star)
  stormheart: 'stormcaller.stormheart', // Q
} as const;
export const STORMCALLER_AURA = {
  stormcell: 'stormcaller.stormcell', // hidden passive: Overvolt upkeep, Static Rush procs
  overvolt: 'stormcaller.overvolt',
  staticRush: 'stormcaller.static_rush',
  caged: 'stormcaller.caged',
  arcWard: 'stormcaller.arc_ward_absorb',
  stormheart: 'stormcaller.stormheart_buff',
} as const;

export const RIMECALLER = {
  rimeTithe: 'rimecaller.rime_tithe', // 1 (Mana Dart)
  hoarlance: 'rimecaller.hoarlance', // 2 (Arcane Lance)
  rimeRing: 'rimecaller.rime_ring', // 3 (Hoarbind)
  frostfold: 'rimecaller.frostfold', // 4 (Fold Step)
  whiteout: 'rimecaller.whiteout', // 5 (Flare Burst)
  frostMantle: 'rimecaller.frost_mantle', // 6 (Glyph Shield)
  wanderingGlacier: 'rimecaller.wandering_glacier', // 7 (Wandering Star)
  deepWinter: 'rimecaller.deep_winter', // Q
} as const;
export const RIMECALLER_AURA = {
  rimecraft: 'rimecaller.rimecraft', // hidden passive: Hoarbite procs
  frozen: 'rimecaller.frozen', // the Fracture marker (lands on bosses too)
  icedIn: 'rimecaller.iced_in', // the root that comes with a freeze (not on cc-immune targets)
  chill: 'rimecaller.chill',
  hoarbite: 'rimecaller.hoarbite',
  brimming: 'rimecaller.brimming', // 5 Spines banked: the next Hoarlance counts as frozen
  frostMantle: 'rimecaller.frost_mantle_absorb',
  deepWinter: 'rimecaller.deep_winter_buff',
} as const;

export const CINDERBORN = {
  emberLash: 'cinderborn.ember_lash', // 1 (Mana Dart)
  cinderfall: 'cinderborn.cinderfall', // 2 (Arcane Lance)
  infernoRing: 'cinderborn.inferno_ring', // 3 (Hoarbind)
  phoenixDive: 'cinderborn.phoenix_dive', // 4 (Fold Step)
  pyreBurst: 'cinderborn.pyre_burst', // 5 (Flare Burst)
  ashrisePact: 'cinderborn.ashrise_pact', // 6 (Glyph Shield)
  wanderingSun: 'cinderborn.wandering_sun', // 7 (Wandering Star)
  kindledHeart: 'cinderborn.kindled_heart', // Q
} as const;
export const CINDERBORN_AURA = {
  pyre: 'cinderborn.pyre', // passive: missing health → damage and lifesteal; kills feed you
  ashrise: 'cinderborn.ashrise', // passive: once a fight, a killing blow → the phoenix (S9)
  phoenix: 'cinderborn.phoenix_form',
  ashesCooling: 'cinderborn.ashes_cooling', // Ashrise spent this fight
  smoulder: 'cinderborn.smoulder',
  dazed: 'cinderborn.inferno_daze',
  ashrisePact: 'cinderborn.ashrise_pact_absorb',
  kindledHeart: 'cinderborn.kindled_heart_buff',
  flashfire: 'cinderborn.flashfire', // proc: Smoulder ticks → the next Ember Lash is instant and free
} as const;

const enemyBolt = { targeting: 'enemy' as const, range: SPELL_RANGE, requiresFacing: true };
const point = (radius: number, maxTargets = 10) => ({ anchor: 'point' as const, shape: { type: 'circle' as const, radius }, affects: 'enemies' as const, maxTargets });
const line = (length: number, width = 3) => ({ anchor: 'caster' as const, shape: { type: 'line' as const, length, width }, affects: 'enemies' as const, maxTargets: 10 });
const absorbAura = (id: string, name: string, extra: Partial<AuraDef> = {}): AuraDef => ({ id, name, kind: 'buff', durationMs: 10_000, stacking: 'refresh', absorb: {}, ...extra });
const gainSelf = (resource: string, amount: number): EffectSpec => ({ type: 'resource', to: 'self', resource, amount });

// ============================================================================
// Stormcaller (ranged dps): Voltage, Overvolt, Skybreak
// ============================================================================

const VOLT = 'voltage';
/** Overvolt starts above 100 Voltage. */
const OVERVOLT_AT = 101;
const overvolting: Condition = { type: 'resourceAtLeast', resource: VOLT, amount: OVERVOLT_AT };

const stormheartBuff: AuraDef = {
  id: STORMCALLER_AURA.stormheart,
  name: 'Stormheart',
  kind: 'buff',
  durationMs: 12_000,
  stacking: 'refresh',
  mods: [{ stat: 'castTimePct', value: -1, abilityIds: [STORMCALLER.spark] }],
  ui: { proc: true },
};

/** Overvolt: the dangerous zone above 100 Voltage. Kept in step with the pool by the Stormcell passive. */
const overvolt: AuraDef = {
  id: STORMCALLER_AURA.overvolt,
  name: 'Overvolt',
  kind: 'buff',
  durationMs: 0,
  stacking: 'refresh',
  mods: [{ stat: 'damageDonePct', value: 0.3 }],
  ui: { glow: { abilityIds: [STORMCALLER.skybreak] } },
};

const staticRush: AuraDef = {
  id: STORMCALLER_AURA.staticRush,
  name: 'Static Rush',
  kind: 'buff',
  durationMs: 15_000,
  stacking: 'refresh',
  mods: [{ stat: 'castTimePct', value: -1, abilityIds: [STORMCALLER.spark] }],
  procs: [{ on: 'castResolved', abilityIds: [STORMCALLER.spark], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [STORMCALLER.spark] } },
};

/** Overvolt on / off from the Voltage pool; `burn` also costs 1% health a second (not inside Stormheart). */
const overvoltCheck = (burn: boolean): EffectSpec => ({
  type: 'conditional',
  cond: overvolting,
  then: [
    { type: 'applyAura', to: 'self', aura: overvolt },
    ...(burn
      ? [
          {
            type: 'damage',
            to: 'self',
            school: 'nature',
            amount: { pctSourceMaxHp: 0.01 },
            selfHarm: true,
            if: { type: 'and', all: [{ type: 'selfInCombat' }, { type: 'selfLacksAura', auraId: STORMCALLER_AURA.stormheart }] },
          } satisfies EffectSpec,
        ]
      : []),
  ],
  else: [{ type: 'removeAura', to: 'self', auraId: STORMCALLER_AURA.overvolt }],
});

/** Hidden passive: Overvolt upkeep every second and after every cast; Sparks may Static Rush. */
const stormcell: AuraDef = {
  id: STORMCALLER_AURA.stormcell,
  name: 'Stormcell',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  periodic: { intervalMs: 1000, effects: [overvoltCheck(true)] },
  procs: [
    { on: 'castResolved', effects: [overvoltCheck(false)] },
    { on: 'castResolved', abilityIds: [STORMCALLER.spark], chance: 0.2, badLuck: { step: 0.06 }, effects: [{ type: 'applyAura', to: 'self', aura: staticRush }] },
  ],
};

const caged: AuraDef = { id: STORMCALLER_AURA.caged, name: 'Static Caged', kind: 'debuff', durationMs: 3000, stacking: 'refresh', cc: 'root' };

const spark: AbilityDef = {
  id: STORMCALLER.spark,
  name: 'Spark',
  school: 'nature',
  description: 'A quick bolt of lightning that builds 12 Voltage. 20% chance to Static Rush: your next Spark is instant and builds 8 more.',
  ...enemyBolt,
  cast: { type: 'cast', timeMs: 1300 },
  gcdMs: 1300,
  gain: [{ resource: VOLT, amount: 12 }],
  effects: [
    { type: 'damage', school: 'nature', amount: { coef: 5.1, variance: 0.05 } },
    { type: 'conditional', cond: { type: 'or', any: [{ type: 'selfHasAura', auraId: STORMCALLER_AURA.staticRush }, { type: 'selfHasAura', auraId: STORMCALLER_AURA.stormheart }] }, then: [gainSelf(VOLT, 8)] },
  ],
  ui: {
    tags: ['builder'],
    color: '#8fb4ff',
    animAs: S.lightningBolt,
    desc: 'A quick bolt of lightning for {damage} Nature damage. Builds 12 Voltage. 20% chance to Static Rush: your next Spark is instant and builds 8 more Voltage. Above 100 Voltage you Overvolt.',
    hint: 'Press 1 to build Voltage: past 100 you Overvolt for big damage, at a cost.',
  },
};

const skybreak: AbilityDef = {
  id: STORMCALLER.skybreak,
  name: 'Skybreak',
  school: 'nature',
  description: 'Vent every point of Voltage (40 at least) as chain lightning: more Voltage, more damage, and one more jump per 25 Voltage.',
  ...enemyBolt,
  spendAll: { resource: VOLT, min: 40 },
  effects: [
    { type: 'damage', school: 'nature', amount: { coef: 1.2, variance: 0.05, perSpent: 0.095 } },
    { type: 'damage', school: 'nature', to: 'area', area: { ...aroundTarget(12), excludePrimary: true, perSpentTargets: { per: 25 } }, amount: { coef: 0.6, variance: 0.05, perSpent: 0.095 }, isAoE: true },
  ],
  ui: {
    tags: ['spender'],
    color: '#c6dcff',
    animAs: S.tempest,
    desc: 'Needs 40 Voltage. Vent it all as chain lightning: {damage} Nature damage to the target, plus 9.5% more for every point of Voltage spent, then it jumps to one more enemy within 12 yd for every 25 Voltage (the jumps deal 60%). Instant.',
    hint: 'Press 2 to vent: at 100 for safety, at 120–130 for the biggest bolt.',
  },
};

const staticCage: AbilityDef = {
  id: STORMCALLER.staticCage,
  name: 'Static Cage',
  school: 'nature',
  description: 'A ring of static bursts out from you: enemies within 10 yd are shocked and rooted for 3 s. Builds 15 Voltage.',
  targeting: 'none',
  cooldown: { durationMs: 20_000 },
  gain: [{ resource: VOLT, amount: 15 }],
  effects: [
    { type: 'damage', school: 'nature', to: 'area', area: aroundSelf(10, 10), amount: { coef: 0.8, variance: 0.05 }, isAoE: true },
    { type: 'applyAura', to: 'area', area: aroundSelf(10, 10), aura: caged },
  ],
  ui: { tags: ['cooldown', 'aoe'], color: '#9ec2ff', animAs: MA.hoarbind, desc: 'A ring of static bursts out from you: every enemy within 10 yd takes {damage} Nature damage and is rooted for 3 sec. Builds 15 Voltage.', hint: 'Press 3 when enemies reach you: a root, and Voltage.' },
};

const staticStep: AbilityDef = {
  id: STORMCALLER.staticStep,
  name: 'Static Step',
  school: 'nature',
  description: 'Blink 15 yd forward as a bolt of lightning, shocking every enemy along the way. Builds 10 Voltage.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  requiresMobility: true,
  usableWhileCasting: true,
  gain: [{ resource: VOLT, amount: 10 }],
  effects: [
    { type: 'damage', school: 'nature', to: 'area', area: line(15), amount: { coef: 0.9, variance: 0.05 }, isAoE: true },
    { type: 'dash', mode: 'forward', distance: 15 },
  ],
  ui: { tags: ['movement'], color: '#b7ccff', animAs: MA.foldStep, desc: 'Blink 15 yd the way you face as a bolt of lightning: every enemy along the line takes {damage} Nature damage. Builds 10 Voltage. Off the global cooldown.', hint: 'Press 4 to blink out of melee and gain Voltage on the way.' },
};

const chainStorm: AbilityDef = {
  id: STORMCALLER.chainStorm,
  name: 'Chain Storm',
  school: 'nature',
  description: 'Call a crackling storm down on the target spot: every enemy within 8 yd is struck. Builds 15 Voltage.',
  targeting: 'ground',
  range: SPELL_RANGE,
  canCastWhileMoving: true,
  cooldown: { durationMs: 8000 },
  gain: [{ resource: VOLT, amount: 15 }],
  effects: [{ type: 'damage', school: 'nature', to: 'area', area: point(8), amount: { coef: 2.8, variance: 0.05 }, isAoE: true }],
  ui: { tags: ['aoe'], color: '#7fa6ff', animAs: S.chainLightning, desc: 'Call a crackling storm down on the target spot: every enemy within 8 yd takes {damage} Nature damage. Builds 15 Voltage. Instant, usable on the move.', hint: 'Press 5 on cooldown: damage and 15 Voltage, even on the move.' },
};

const arcWardAura: AuraDef = absorbAura(STORMCALLER_AURA.arcWard, 'Arc Ward', {
  // Every hit the ward takes is grounded into you as Voltage.
  procs: [{ on: 'damageTaken', icdMs: 400, effects: [gainSelf(VOLT, 5)] }],
});

const arcWard: AbilityDef = {
  id: STORMCALLER.arcWard,
  name: 'Arc Ward',
  school: 'nature',
  description: 'A crackling ward absorbs damage equal to 30% of your maximum health for 10 s; every hit it takes grounds into you as 5 Voltage.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 30_000 },
  effects: [{ type: 'absorb', to: 'self', auraId: STORMCALLER_AURA.arcWard, amount: { pctSourceMaxHp: 0.3 } }],
  ui: { tags: ['defensive'], color: '#9fb6ff', animAs: S.astralShift, desc: 'A crackling ward absorbs {absorb} damage (30% of your maximum health) for 10 sec. Every hit it takes grounds into you as 5 Voltage. Off the global cooldown.', hint: 'Press 6 before a big hit: the blow becomes Voltage.' },
};

const wanderingTempest: AbilityDef = {
  id: STORMCALLER.wanderingTempest,
  name: 'Wandering Tempest',
  school: 'nature',
  description: 'Your Wandering Star, overcharged: a storm orb drifts 30 yd over 3 s, arcing into every enemy within 5 yd (each zap builds 3 Voltage), then bursts.',
  targeting: 'none',
  range: 30,
  cooldown: { durationMs: 20_000 },
  effects: [
    {
      type: 'zone',
      zoneId: STORMCALLER.wanderingTempest,
      anchor: 'caster',
      shape: { type: 'circle', radius: 5 },
      durationMs: 3000,
      affects: 'enemies',
      uniquePerCaster: true,
      motion: { type: 'linear', speed: 10, heading: 'target' },
      tick: { intervalMs: 500, effects: [{ type: 'damage', school: 'nature', amount: { coef: 1.05, variance: 0.05 }, isAoE: true }, gainSelf(VOLT, 3)] },
      onExpire: [{ type: 'damage', school: 'nature', to: 'area', area: point(9, 12), amount: { coef: 3.8, variance: 0.05 }, isAoE: true }],
    },
  ],
  ui: {
    tags: ['aoe', 'cooldown'],
    color: '#b9c9ff',
    animAs: MA.wanderingStar,
    desc: 'Your Wandering Star, overcharged. A storm orb drifts toward your target (or ahead of you) for 3 sec, arcing into every enemy within 5 yd for {damage} Nature damage every 0.5 sec, and every zap builds 3 Voltage. Then it bursts for {damage2} Nature damage to every enemy within 9 yd. Instant.',
    hint: 'Press 7 through a pack: a bigger star that charges you up as it zaps.',
  },
};

const stormheart: AbilityDef = {
  id: STORMCALLER.stormheart,
  name: 'Stormheart',
  school: 'nature',
  description: 'Become the storm for 12 s: +30 Voltage now, Sparks are instant and build 8 more Voltage, and Overvolt costs no health.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 60_000 },
  gain: [{ resource: VOLT, amount: 30 }],
  effects: [{ type: 'applyAura', to: 'self', aura: stormheartBuff }],
  ui: { tags: ['cooldown'], color: '#5f8cff', animAs: S.stormkeeper, desc: 'Gain 30 Voltage. For 12 sec your Sparks are instant and build 8 more Voltage, and Overvolt costs no health: ride it all the way. Off the global cooldown.', hint: 'Press Q for a long, free Overvolt.' },
};

export const stormcallerSpec: SpecDef = {
  id: MAGE_SPEC.stormcaller,
  baseClassId: 'mage',
  name: 'Stormcaller',
  role: 'dps',
  unlockLevel: 10,
  evolutions: {
    [MA.manaDart]: STORMCALLER.spark,
    [MA.arcaneLance]: STORMCALLER.skybreak,
    [MA.hoarbind]: STORMCALLER.staticCage,
    [MA.foldStep]: STORMCALLER.staticStep,
    [MA.flareBurst]: STORMCALLER.chainStorm,
    [MA.glyphShield]: STORMCALLER.arcWard,
    [MA.wanderingStar]: STORMCALLER.wanderingTempest,
  },
  extraAbilities: [{ key: 'Q', abilityId: STORMCALLER.stormheart }],
  abilities: [spark, skybreak, staticCage, staticStep, chainStorm, arcWard, wanderingTempest, stormheart],
  // Voltage is the whole bar (no mana): 0–130, bleeds off out of combat.
  resources: [{ id: VOLT, name: 'Voltage', max: 130, start: 0, regenPerSecOoc: -5, oocSettle: 0 }],
  passives: [stormcell],
  auras: [overvolt, staticRush, caged, arcWardAura, stormheartBuff],
  color: '#4f7dff',
  ui: {
    tagline: 'Overcharges until the air itself is a weapon. Risk it or vent it.',
    fantasy: 'Static builds in you with every cast. Vent it for a huge chain-lightning burst, or push past the limit for bonus power and risk overvolting yourself.',
    signature: 'Voltage 0–130: above 100 you Overvolt (+30% damage, but you lose 1% health a second); Skybreak vents it all as chain lightning, one jump per 25.',
    weapon: 'storm_staff',
    tint: '#4f7dff',
    glow: '#bcd4ff',
  },
};

// ============================================================================
// Rimecaller (ranged dps): Frost Spines, Frozen, Fracture
// ============================================================================

const SPINES = 'spines';

/** The Fracture marker: Hoarlance always crits a Frozen target. No cc, so it lands on bosses too. */
const frozen: AuraDef = { id: RIMECALLER_AURA.frozen, name: 'Frozen', kind: 'debuff', durationMs: 3000, stacking: 'refresh', mods: [{ stat: 'moveSpeedPct', value: -0.5 }] };
const icedIn: AuraDef = { id: RIMECALLER_AURA.icedIn, name: 'Iced In', kind: 'debuff', durationMs: 3000, stacking: 'refresh', cc: 'root' };
const chill: AuraDef = { id: RIMECALLER_AURA.chill, name: 'Rimechill', kind: 'debuff', durationMs: 6000, stacking: 'refresh', mods: [{ stat: 'moveSpeedPct', value: -0.3 }] };

/** Freeze for `ms` (to: the primary target, or an area). */
const freeze = (ms: number, area?: EffectSpec['area']): EffectSpec[] => [
  { type: 'applyAura', ...(area ? { to: 'area' as const, area } : {}), aura: frozen, durationMs: ms },
  { type: 'applyAura', ...(area ? { to: 'area' as const, area } : {}), aura: icedIn, durationMs: ms },
];

const hoarbite: AuraDef = {
  id: RIMECALLER_AURA.hoarbite,
  name: 'Hoarbite',
  kind: 'buff',
  durationMs: 15_000,
  stacking: 'refresh',
  // Spent by the next Hoarlance, unless that target was Frozen anyway.
  procs: [{ on: 'castResolved', abilityIds: [RIMECALLER.hoarlance], consumeStack: true, if: { type: 'targetLacksAura', auraId: RIMECALLER_AURA.frozen }, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [RIMECALLER.hoarlance] } },
};

const brimming: AuraDef = {
  id: RIMECALLER_AURA.brimming,
  name: 'Brimming',
  kind: 'buff',
  durationMs: 0,
  stacking: 'refresh',
  flags: { removeOutOfCombat: true },
  ui: { glow: { abilityIds: [RIMECALLER.hoarlance] } },
};

const deepWinterBuff: AuraDef = {
  id: RIMECALLER_AURA.deepWinter,
  name: 'Deep Winter',
  kind: 'buff',
  durationMs: 15_000,
  stacking: 'stack',
  maxStacks: 3,
  mods: [{ stat: 'critChanceBonus', value: 1, abilityIds: [RIMECALLER.hoarlance], ignoreStacks: true }],
  procs: [{ on: 'castResolved', abilityIds: [RIMECALLER.hoarlance], consumeStack: true, effects: [] }],
  ui: { proc: true, stackCounterOn: [RIMECALLER.hoarlance] },
};

/** Hidden passive: Rime Tithe may bite (Hoarbite, bad-luck protected). */
const rimecraft: AuraDef = {
  id: RIMECALLER_AURA.rimecraft,
  name: 'Rimecraft',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  procs: [{ on: 'castResolved', abilityIds: [RIMECALLER.rimeTithe], chance: 0.15, badLuck: { step: 0.05 }, effects: [{ type: 'applyAura', to: 'self', aura: hoarbite }] }],
};

/** Five Spines banked: Brimming (the next Hoarlance counts as frozen). */
const brimCheck: EffectSpec = { type: 'conditional', cond: { type: 'resourceAtLeast', resource: SPINES, amount: 5 }, then: [{ type: 'applyAura', to: 'self', aura: brimming }] };
const fracturing: Condition = {
  type: 'or',
  any: [{ type: 'targetHasAura', auraId: RIMECALLER_AURA.frozen }, { type: 'selfHasAura', auraId: RIMECALLER_AURA.hoarbite }, { type: 'selfHasAura', auraId: RIMECALLER_AURA.brimming }],
};

const rimeTithe: AbilityDef = {
  id: RIMECALLER.rimeTithe,
  name: 'Rime Tithe',
  school: 'frost',
  description: 'A bolt of hoarfrost that chills the target and banks a Frost Spine. 15% chance for Hoarbite: your next Hoarlance fractures as if the target were frozen.',
  ...enemyBolt,
  cast: { type: 'cast', timeMs: 1600 },
  gcdMs: 1600,
  cost: [{ resource: 'mana', amount: 150 }],
  gain: [{ resource: SPINES, amount: 1 }],
  effects: [{ type: 'damage', school: 'frost', amount: { coef: 4.65, variance: 0.05 } }, { type: 'applyAura', aura: chill }, brimCheck],
  ui: {
    tags: ['builder'],
    color: '#9ddcff',
    animAs: MA.manaDart,
    desc: 'A bolt of hoarfrost for {damage} Frost damage that slows the target by 30% and banks a Frost Spine (up to 5). 15% chance for Hoarbite: your next Hoarlance fractures as if the target were frozen.',
    hint: 'Press 1 to bank Frost Spines for Hoarlance.',
  },
};

const hoarlance: AbilityDef = {
  id: RIMECALLER.hoarlance,
  name: 'Hoarlance',
  school: 'frost',
  description: 'Launch every banked Frost Spine as one glacial lance. Always crits a Frozen target (Fracture). A full volley of 5 counts as frozen and freezes the target.',
  ...enemyBolt,
  spendAll: { resource: SPINES, min: 1 },
  effects: [
    {
      type: 'conditional',
      cond: fracturing,
      then: [{ type: 'damage', school: 'frost', amount: { coef: 1.75, variance: 0.05, perSpent: 0.9 }, critBonus: 1 }],
      else: [{ type: 'damage', school: 'frost', amount: { coef: 1.75, variance: 0.05, perSpent: 0.9 } }],
    },
    { type: 'conditional', cond: { type: 'selfHasAura', auraId: RIMECALLER_AURA.brimming }, then: [...freeze(2000), { type: 'removeAura', to: 'self', auraId: RIMECALLER_AURA.brimming }] },
  ],
  ui: {
    tags: ['spender'],
    color: '#cdeeff',
    animAs: MA.arcaneLance,
    desc: 'Needs 1 Frost Spine. Launch them all as one lance: {damage} Frost damage, +90% per Spine. Fracture: it always crits a Frozen target, after a Hoarbite, or with a full volley of 5 (which also freezes the target for 2 sec). Instant.',
    hint: 'Press 2 into a freeze, on a Hoarbite, or at 5 Spines: guaranteed crits.',
  },
};

const rimeRing: AbilityDef = {
  id: RIMECALLER.rimeRing,
  name: 'Rime Ring',
  school: 'frost',
  description: 'A ring of rime bursts out from you: enemies within 10 yd are frozen solid for 4 s.',
  targeting: 'none',
  cooldown: { durationMs: 20_000 },
  cost: [{ resource: 'mana', amount: 200 }],
  effects: [{ type: 'damage', school: 'frost', to: 'area', area: aroundSelf(10, 10), amount: { coef: 0.7, variance: 0.05 }, isAoE: true }, ...freeze(4000, aroundSelf(10, 10))],
  ui: { tags: ['cooldown', 'aoe'], color: '#a9e4ff', animAs: MA.hoarbind, desc: 'A ring of rime bursts out from you: every enemy within 10 yd takes {damage} Frost damage and is Frozen for 4 sec (rooted; Hoarlance fractures it).', hint: 'Press 3 when a pack reaches you, then Hoarlance into the freeze.' },
};

const frostfold: AbilityDef = {
  id: RIMECALLER.frostfold,
  name: 'Frostfold',
  school: 'frost',
  description: 'Fold 15 yd forward, leaving a burst of frost where you stood that freezes enemies within 8 yd for 3 s.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  requiresMobility: true,
  usableWhileCasting: true,
  effects: [{ type: 'damage', school: 'frost', to: 'area', area: aroundSelf(8, 10), amount: { coef: 0.5, variance: 0.05 }, isAoE: true }, ...freeze(3000, aroundSelf(8, 10)), { type: 'dash', mode: 'forward', distance: 15 }],
  ui: { tags: ['movement'], color: '#bfe9ff', animAs: MA.foldStep, desc: 'Blink 15 yd the way you face. Where you stood, frost bursts: every enemy within 8 yd takes {damage} Frost damage and is Frozen for 3 sec. Off the global cooldown.', hint: 'Press 4 to escape melee and freeze whatever chased you.' },
};

const whiteoutTick: EffectSpec[] = [
  { type: 'damage', school: 'frost', amount: { coef: 0.95, variance: 0.05 }, isAoE: true },
  { type: 'conditional', cond: { type: 'chance', p: 0.15 }, then: freeze(2000) },
];

const whiteout: AbilityDef = {
  id: RIMECALLER.whiteout,
  name: 'Whiteout',
  school: 'frost',
  description: 'A blinding snowstorm on the target spot for 6 s: enemies inside take frost damage every second and may freeze. Banks a Frost Spine.',
  targeting: 'ground',
  range: SPELL_RANGE,
  canCastWhileMoving: true,
  cooldown: { durationMs: 12_000 },
  cost: [{ resource: 'mana', amount: 300 }],
  gain: [{ resource: SPINES, amount: 1 }],
  effects: [
    { type: 'zone', zoneId: RIMECALLER.whiteout, anchor: 'point', shape: { type: 'circle', radius: 8 }, durationMs: 6000, affects: 'enemies', tick: { intervalMs: 1000, effects: whiteoutTick }, snapshot: true },
    brimCheck,
  ],
  ui: { tags: ['aoe'], color: '#e4f6ff', animAs: S.earthquake, desc: 'A blinding snowstorm on the target spot for 6 sec: every enemy within 8 yd takes {damage} Frost damage every second, and each tick has a 15% chance to freeze it for 2 sec. Banks a Frost Spine. Instant, usable on the move.', hint: 'Press 5 on cooldown: a storm that freezes things for your Hoarlance.' },
};

const frostMantleAura: AuraDef = absorbAura(RIMECALLER_AURA.frostMantle, 'Frost Mantle', {
  // Whoever hits the mantle is chilled.
  procs: [{ on: 'damageTaken', icdMs: 300, effects: [{ type: 'applyAura', aura: chill }] }],
});

const frostMantle: AbilityDef = {
  id: RIMECALLER.frostMantle,
  name: 'Frost Mantle',
  school: 'frost',
  description: 'A mantle of ice absorbs damage equal to 35% of your maximum health for 10 s; enemies that strike it are chilled.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 25_000 },
  effects: [{ type: 'absorb', to: 'self', auraId: RIMECALLER_AURA.frostMantle, amount: { pctSourceMaxHp: 0.35 } }],
  ui: { tags: ['defensive'], color: '#bde8ff', animAs: MA.glyphShield, desc: 'A mantle of ice absorbs {absorb} damage (35% of your maximum health) for 10 sec. Every enemy that strikes it is chilled (30% slower for 6 sec). Off the global cooldown.', hint: 'Press 6 before a big hit; attackers slow down.' },
};

const wanderingGlacier: AbilityDef = {
  id: RIMECALLER.wanderingGlacier,
  name: 'Wandering Glacier',
  school: 'frost',
  description: 'Your Wandering Star, frozen: a glacier shard grinds 30 yd forward over 3.5 s, chilling everything within 5 yd, then shatters and freezes everything within 9 yd. Banks Frost Spines.',
  targeting: 'none',
  range: 30,
  cooldown: { durationMs: 20_000 },
  gain: [{ resource: SPINES, amount: 1 }],
  effects: [
    {
      type: 'zone',
      zoneId: RIMECALLER.wanderingGlacier,
      anchor: 'caster',
      shape: { type: 'circle', radius: 5 },
      durationMs: 3500,
      affects: 'enemies',
      uniquePerCaster: true,
      motion: { type: 'linear', speed: 8.5, heading: 'target' },
      tick: { intervalMs: 500, effects: [{ type: 'damage', school: 'frost', amount: { coef: 0.95, variance: 0.05 }, isAoE: true }, { type: 'applyAura', aura: chill }] },
      onExpire: [{ type: 'damage', school: 'frost', to: 'area', area: point(9, 12), amount: { coef: 3.6, variance: 0.05 }, isAoE: true }, ...freeze(2500, point(9, 12)), gainSelf(SPINES, 1), brimCheck],
    },
    brimCheck,
  ],
  ui: {
    tags: ['aoe', 'cooldown'],
    color: '#c9ecff',
    animAs: MA.wanderingStar,
    desc: 'Your Wandering Star, frozen. A glacier shard grinds toward your target (or ahead of you) for 3.5 sec, hitting every enemy within 5 yd for {damage} Frost damage every 0.5 sec and chilling it, then shatters for {damage2} Frost damage and freezes every enemy within 9 yd for 2.5 sec. Banks a Frost Spine on cast and another when it shatters. Instant.',
    hint: 'Press 7 through a pack: the shatter freezes them all for your Hoarlance.',
  },
};

const deepWinter: AbilityDef = {
  id: RIMECALLER.deepWinter,
  name: 'Deep Winter',
  school: 'frost',
  description: 'Freeze every enemy within 12 yd of your target for 4 s and bank 2 Frost Spines; your next 3 Hoarlances crit.',
  ...enemyBolt,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 60_000 },
  gain: [{ resource: SPINES, amount: 2 }],
  effects: [...freeze(4000, aroundTarget(12, 12)), { type: 'applyAura', to: 'self', aura: deepWinterBuff, stacks: 3 }, brimCheck],
  ui: { tags: ['cooldown'], color: '#7fd0ff', animAs: S.stormkeeper, desc: 'Freeze every enemy within 12 yd of your target for 4 sec and bank 2 Frost Spines. Your next 3 Hoarlances within 15 sec are guaranteed crits. Off the global cooldown.', hint: 'Press Q for a burst of fractured lances.' },
};

export const rimecallerSpec: SpecDef = {
  id: MAGE_SPEC.rimecaller,
  baseClassId: 'mage',
  name: 'Rimecaller',
  role: 'dps',
  unlockLevel: 10,
  evolutions: {
    [MA.manaDart]: RIMECALLER.rimeTithe,
    [MA.arcaneLance]: RIMECALLER.hoarlance,
    [MA.hoarbind]: RIMECALLER.rimeRing,
    [MA.foldStep]: RIMECALLER.frostfold,
    [MA.flareBurst]: RIMECALLER.whiteout,
    [MA.glyphShield]: RIMECALLER.frostMantle,
    [MA.wanderingStar]: RIMECALLER.wanderingGlacier,
  },
  extraAbilities: [{ key: 'Q', abilityId: RIMECALLER.deepWinter }],
  abilities: [rimeTithe, hoarlance, rimeRing, frostfold, whiteout, frostMantle, wanderingGlacier, deepWinter],
  // Mana as before; Frost Spines replace Arcana as the pips (they melt slowly out of combat).
  resources: [
    { id: 'mana', name: 'Mana', max: 10000, start: 10000, regenPerSec: 140, regenPerSecOoc: 600 },
    { id: SPINES, name: 'Frost Spines', max: 5, start: 0, regenPerSecOoc: -0.1, oocSettle: 0 },
  ],
  passives: [rimecraft],
  auras: [frozen, icedIn, chill, hoarbite, brimming, deepWinterBuff, frostMantleAura],
  color: '#7fd0ff',
  ui: {
    tagline: 'Freezes enemies solid, then fractures them for huge crits.',
    fantasy: 'A mage of winter. You lock packs in ice, bank frost spines over your shoulder, and launch a glacial lance that fractures everything frozen.',
    signature: 'Frost Spines (0–5) bank on Rime Tithe and fire all at once with Hoarlance, which always crits a Frozen target (Fracture). Hoarbite procs and a full volley of 5 count as frozen.',
    weapon: 'ice_staff',
    tint: '#7fd0ff',
    glow: '#e2f6ff',
  },
};

// ============================================================================
// Cinderborn (ranged dps): health as fuel, Pyre, Ashrise
// ============================================================================

const CINDERS = 'cinders';
/** Spells are free in phoenix form and inside Kindled Heart. */
const paysHealth: Condition = { type: 'and', all: [{ type: 'selfLacksAura', auraId: CINDERBORN_AURA.phoenix }, { type: 'selfLacksAura', auraId: CINDERBORN_AURA.kindledHeart }] };
/** Burn `pct` of your maximum health (never lethal; free as a phoenix or in Kindled Heart, and `alsoFree` when given). */
const burn = (pct: number, alsoFree?: string): EffectSpec => ({
  type: 'damage',
  to: 'self',
  school: 'fire',
  amount: { pctSourceMaxHp: pct },
  selfHarm: true,
  if: alsoFree ? { type: 'and', all: [paysHealth, { type: 'selfLacksAura', auraId: alsoFree }] } : paysHealth,
});

const flashfire: AuraDef = {
  id: CINDERBORN_AURA.flashfire,
  name: 'Flashfire',
  kind: 'buff',
  durationMs: 12_000,
  stacking: 'refresh',
  mods: [{ stat: 'castTimePct', value: -1, abilityIds: [CINDERBORN.emberLash] }],
  procs: [{ on: 'castResolved', abilityIds: [CINDERBORN.emberLash], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [CINDERBORN.emberLash] } },
};

const smoulder: AuraDef = {
  id: CINDERBORN_AURA.smoulder,
  name: 'Smoulder',
  kind: 'debuff',
  durationMs: 8000,
  stacking: 'refresh',
  pandemic: true,
  periodic: { intervalMs: 2000, effects: [{ type: 'damage', school: 'fire', amount: { coef: 0.4 } }] },
};

const phoenixForm: AuraDef = {
  id: CINDERBORN_AURA.phoenix,
  name: 'Phoenix Form',
  kind: 'buff',
  durationMs: 10_000,
  stacking: 'refresh',
  mods: [{ stat: 'damageDonePct', value: 0.3 }],
  flags: { cannotDie: true },
  // The phoenix lands: heal 40% of your maximum health.
  procs: [{ on: 'auraExpire', effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.4 } }] }],
  ui: { proc: true },
};

const ashesCooling: AuraDef = { id: CINDERBORN_AURA.ashesCooling, name: 'Ashes Cooling', kind: 'debuff', durationMs: 0, stacking: 'refresh', flags: { removeOutOfCombat: true } };

/** Once a fight, a killing blow instead leaves you at 1 HP as a phoenix (S9). Re-arms out of combat. */
const ashrise: AuraDef = {
  id: CINDERBORN_AURA.ashrise,
  name: 'Ashrise',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { cheatDeath: true, persistThroughDeath: true },
  onCheatDeath: [
    { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.1 } },
    { type: 'applyAura', to: 'self', aura: phoenixForm },
    { type: 'applyAura', to: 'self', aura: ashesCooling },
  ],
};

/** The signature passive: missing health → damage and lifesteal; every kill feeds you. */
const pyre: AuraDef = {
  id: CINDERBORN_AURA.pyre,
  name: 'Pyreblood',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  mods: [
    { stat: 'damageDonePct', value: 0.5, perMissingHp: true },
    { stat: 'leechPct', value: 0.36, perMissingHp: true },
  ],
  flags: { persistThroughDeath: true },
  procs: [
    { on: 'killCredit', effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.08 } }] },
    // Phoenix Dive and the Wandering Sun feed you for every enemy they burn.
    { on: 'damageDealt', abilityIds: [CINDERBORN.phoenixDive], effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.03 } }] },
    { on: 'damageDealt', abilityIds: [CINDERBORN.wanderingSun], effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.008 } }] },
    // Smouldering ticks may flash over: the next Ember Lash is instant and free.
    { on: 'damageDealt', abilityIds: [CINDERBORN_AURA.smoulder], chance: 0.15, badLuck: { step: 0.04 }, effects: [{ type: 'applyAura', to: 'self', aura: flashfire }] },
  ],
};

const kindledHeartBuff: AuraDef = {
  id: CINDERBORN_AURA.kindledHeart,
  name: 'Kindled Heart',
  kind: 'buff',
  durationMs: 10_000,
  stacking: 'refresh',
  mods: [{ stat: 'hastePct', value: 0.15 }],
  ui: { proc: true },
};

const dazed: AuraDef = { id: CINDERBORN_AURA.dazed, name: 'Inferno Daze', kind: 'debuff', durationMs: 2000, stacking: 'refresh', cc: 'disorient' };

/** Kindled Heart: Ember Lash and Cinderfall leave burning ground where they land. */
const kindledGround = (radius: number): EffectSpec => ({
  type: 'conditional',
  cond: { type: 'selfHasAura', auraId: CINDERBORN_AURA.kindledHeart },
  then: [{ type: 'zone', zoneId: 'cinderborn.kindled_ground', anchor: 'target', shape: { type: 'circle', radius }, durationMs: 4000, affects: 'enemies', uniquePerCaster: false, tick: { intervalMs: 1000, effects: [{ type: 'damage', school: 'fire', amount: { coef: 0.35 }, isAoE: true }] }, snapshot: true }],
});

const emberLash: AbilityDef = {
  id: CINDERBORN.emberLash,
  name: 'Ember Lash',
  school: 'fire',
  description: 'Lash the target with your own burning blood: costs 1.2% of your health, sets it Smouldering and builds a Cinder.',
  ...enemyBolt,
  cast: { type: 'cast', timeMs: 1500 },
  gain: [{ resource: CINDERS, amount: 1 }],
  effects: [{ type: 'damage', school: 'fire', amount: { coef: 4.65, variance: 0.05 } }, { type: 'applyAura', aura: smoulder }, kindledGround(5), burn(0.012, CINDERBORN_AURA.flashfire)],
  ui: {
    tags: ['builder'],
    color: '#ff8a4a',
    animAs: S.flameShock,
    desc: 'Costs 1.2% of your health. Lash the target for {damage} Fire damage and set it Smouldering (Fire damage every 2 sec for 8 sec). Builds 1 Cinder. Smouldering ticks may Flashfire: your next Ember Lash is instant and free.',
    hint: 'Press 1 to build Cinders: it costs a sliver of health, not mana.',
  },
};

const cinderfall: AbilityDef = {
  id: CINDERBORN.cinderfall,
  name: 'Cinderfall',
  school: 'fire',
  description: 'Spend 3 Cinders: call a meteor of your own fire down on the target. Above 60% health it takes 12% of your health for a huge crater; below, 6% for a smaller one.',
  ...enemyBolt,
  cost: [{ resource: CINDERS, amount: 3 }],
  effects: [
    {
      type: 'conditional',
      cond: { type: 'not', cond: { type: 'selfHpBelow', pct: 0.6 } },
      then: [{ type: 'damage', school: 'fire', amount: { coef: 8.5, variance: 0.05 } }, { type: 'damage', school: 'fire', to: 'area', area: { ...aroundTarget(8, 8), excludePrimary: true }, amount: { coef: 4.2, variance: 0.05 }, isAoE: true }, burn(0.12)],
      else: [{ type: 'damage', school: 'fire', amount: { coef: 5.5, variance: 0.05 } }, { type: 'damage', school: 'fire', to: 'area', area: { ...aroundTarget(6, 6), excludePrimary: true }, amount: { coef: 2.8, variance: 0.05 }, isAoE: true }, burn(0.06)],
    },
    kindledGround(8),
  ],
  ui: {
    tags: ['spender', 'aoe'],
    color: '#ff5a1f',
    animAs: S.lavaBurst,
    desc: 'Spend 3 Cinders: a meteor of your own fire. Above 60% health it costs 12% of your health and deals {damage} Fire damage to the target and half that to every other enemy within 8 yd; below 60% it costs 6% and deals {damage2}, splashing within 6 yd. Instant.',
    hint: 'Press 2 at 3 Cinders: the big crater while you are healthy.',
  },
};

const infernoRing: AbilityDef = {
  id: CINDERBORN.infernoRing,
  name: 'Inferno Ring',
  school: 'fire',
  description: 'Costs 3% of your health: a ring of fire bursts out from you, burning and disorienting enemies within 10 yd for 2 s.',
  targeting: 'none',
  cooldown: { durationMs: 20_000 },
  effects: [
    { type: 'damage', school: 'fire', to: 'area', area: aroundSelf(10, 10), amount: { coef: 1.0, variance: 0.05 }, isAoE: true },
    { type: 'applyAura', to: 'area', area: aroundSelf(10, 10), aura: dazed },
    burn(0.03),
  ],
  ui: { tags: ['cooldown', 'aoe'], color: '#ff7a2e', animAs: MA.hoarbind, desc: 'Costs 3% of your health. A ring of fire bursts out from you: every enemy within 10 yd takes {damage} Fire damage and is disoriented for 2 sec.', hint: 'Press 3 when enemies reach you, then step away.' },
};

const phoenixDive: AbilityDef = {
  id: CINDERBORN.phoenixDive,
  name: 'Phoenix Dive',
  school: 'fire',
  description: 'Dive 15 yd forward in a sheet of flame, burning every enemy you pass through and healing 3% of your health for each one.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  requiresMobility: true,
  usableWhileCasting: true,
  effects: [{ type: 'damage', school: 'fire', to: 'area', area: line(15, 4), amount: { coef: 1.2, variance: 0.05 }, isAoE: true }, { type: 'dash', mode: 'forward', distance: 15, speed: 45 }],
  ui: { tags: ['movement', 'heal'], color: '#ffb35a', animAs: MA.foldStep, desc: 'Dive 15 yd the way you face in a sheet of flame: every enemy along the way takes {damage} Fire damage, and you heal 3% of your maximum health for each one. Off the global cooldown.', hint: 'Press 4 through a pack: escape and heal at once.' },
};

const pyreBurst: AbilityDef = {
  id: CINDERBORN.pyreBurst,
  name: 'Pyre Burst',
  school: 'fire',
  description: 'Costs 4% of your health: detonate a pyre at the target spot, burning every enemy within 8 yd and leaving the ground on fire for 6 s.',
  targeting: 'ground',
  range: SPELL_RANGE,
  canCastWhileMoving: true,
  cooldown: { durationMs: 8000 },
  effects: [
    { type: 'damage', school: 'fire', to: 'area', area: point(8), amount: { coef: 2.8, variance: 0.05 }, isAoE: true },
    { type: 'zone', zoneId: CINDERBORN.pyreBurst, anchor: 'point', shape: { type: 'circle', radius: 8 }, durationMs: 6000, affects: 'enemies', tick: { intervalMs: 1000, effects: [{ type: 'damage', school: 'fire', amount: { coef: 0.4 }, isAoE: true }] }, snapshot: true },
    burn(0.04),
  ],
  ui: { tags: ['aoe'], color: '#ff6a2a', animAs: MA.flareBurst, desc: 'Costs 4% of your health. Detonate a pyre at the target spot: every enemy within 8 yd takes {damage} Fire damage, and the ground burns for {damage2} Fire damage a second for 6 sec. Instant, usable on the move.', hint: 'Press 5 on cooldown: a burst and burning ground.' },
};

const ashrisePactAura: AuraDef = absorbAura(CINDERBORN_AURA.ashrisePact, 'Ashrise Pact');

const ashrisePact: AbilityDef = {
  id: CINDERBORN.ashrisePact,
  name: 'Ashrise Pact',
  school: 'fire',
  description: 'A shell of hot ash absorbs damage equal to 25% of your maximum health for 10 s. If Ashrise was spent this fight, it is ready again.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 60_000 },
  effects: [
    { type: 'absorb', to: 'self', auraId: CINDERBORN_AURA.ashrisePact, amount: { pctSourceMaxHp: 0.25 } },
    {
      type: 'conditional',
      cond: { type: 'selfHasAura', auraId: CINDERBORN_AURA.ashesCooling },
      then: [
        { type: 'removeAura', to: 'self', auraId: CINDERBORN_AURA.ashesCooling },
        { type: 'removeAura', to: 'self', auraId: CINDERBORN_AURA.ashrise },
        { type: 'applyAura', to: 'self', aura: ashrise },
      ],
    },
  ],
  ui: { tags: ['defensive'], color: '#ffa04a', animAs: MA.glyphShield, desc: 'A shell of hot ash absorbs {absorb} damage (25% of your maximum health) for 10 sec. If Ashrise was spent this fight, the pact makes it ready again. Off the global cooldown.', hint: 'Press 6 when low, or after you have risen once.' },
};

const wanderingSun: AbilityDef = {
  id: CINDERBORN.wanderingSun,
  name: 'Wandering Sun',
  school: 'fire',
  description: 'Your Wandering Star, ablaze: a small sun drifts 30 yd over 3 s, scorching everything within 5 yd (each scorch heals you), setting it Smouldering, then bursts. Costs no health.',
  targeting: 'none',
  range: 30,
  cooldown: { durationMs: 20_000 },
  effects: [
    {
      type: 'zone',
      zoneId: CINDERBORN.wanderingSun,
      anchor: 'caster',
      shape: { type: 'circle', radius: 5 },
      durationMs: 3000,
      affects: 'enemies',
      uniquePerCaster: true,
      motion: { type: 'linear', speed: 10, heading: 'target' },
      tick: { intervalMs: 500, effects: [{ type: 'damage', school: 'fire', amount: { coef: 1.0, variance: 0.05 }, isAoE: true }, { type: 'applyAura', aura: smoulder }] },
      onExpire: [{ type: 'damage', school: 'fire', to: 'area', area: point(9, 12), amount: { coef: 3.8, variance: 0.05 }, isAoE: true }],
    },
  ],
  ui: {
    tags: ['aoe', 'cooldown', 'heal'],
    color: '#ffc46a',
    animAs: MA.wanderingStar,
    desc: 'Your Wandering Star, ablaze. A small sun drifts toward your target (or ahead of you) for 3 sec, scorching every enemy within 5 yd for {damage} Fire damage every 0.5 sec and setting it Smouldering; every scorch heals you for 0.8% of your maximum health. Then it bursts for {damage2} Fire damage within 9 yd. Costs no health. Instant.',
    hint: 'Press 7 through a pack: the more it burns, the more it heals you.',
  },
};

const kindledHeart: AbilityDef = {
  id: CINDERBORN.kindledHeart,
  name: 'Kindled Heart',
  school: 'fire',
  description: 'For 10 s your spells cost no health, you cast 15% faster, and Ember Lash and Cinderfall leave burning ground.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 60_000 },
  gain: [{ resource: CINDERS, amount: 1 }],
  effects: [{ type: 'applyAura', to: 'self', aura: kindledHeartBuff }],
  ui: { tags: ['cooldown'], color: '#ff4a1a', animAs: S.stormkeeper, desc: 'Gain a Cinder. For 10 sec your spells cost no health, you cast 15% faster, and every Ember Lash and Cinderfall leaves burning ground for 4 sec. Off the global cooldown.', hint: 'Press Q for a free, blazing burst.' },
};

export const cinderbornSpec: SpecDef = {
  id: MAGE_SPEC.cinderborn,
  baseClassId: 'mage',
  name: 'Cinderborn',
  role: 'dps',
  unlockLevel: 10,
  evolutions: {
    [MA.manaDart]: CINDERBORN.emberLash,
    [MA.arcaneLance]: CINDERBORN.cinderfall,
    [MA.hoarbind]: CINDERBORN.infernoRing,
    [MA.foldStep]: CINDERBORN.phoenixDive,
    [MA.flareBurst]: CINDERBORN.pyreBurst,
    [MA.glyphShield]: CINDERBORN.ashrisePact,
    [MA.wanderingStar]: CINDERBORN.wanderingSun,
  },
  extraAbilities: [{ key: 'Q', abilityId: CINDERBORN.kindledHeart }],
  abilities: [emberLash, cinderfall, infernoRing, phoenixDive, pyreBurst, ashrisePact, wanderingSun, kindledHeart],
  // No mana: health is the fuel. Cinders (0–3) replace Arcana as the builder pips.
  resources: [{ id: CINDERS, name: 'Cinders', max: 3, start: 0, regenPerSecOoc: -0.5, oocSettle: 0 }],
  passives: [pyre, ashrise],
  auras: [smoulder, phoenixForm, ashesCooling, kindledHeartBuff, dazed, ashrisePactAura, flashfire],
  color: '#ff6a2a',
  ui: {
    tagline: 'Burns its own life as fuel and rises from the ash once a fight.',
    fantasy: 'Fire costs health, not mana. Your strongest spells eat your own body, and when you finally go down you erupt as a phoenix and keep fighting.',
    signature: 'Pyreblood: fire costs health, and missing health becomes damage and lifesteal; kills heal you. Once a fight a killing blow triggers Ashrise, a 10 s phoenix form.',
    weapon: 'ember_staff',
    tint: '#ff6a2a',
    glow: '#ffc07a',
  },
};

/** Implemented Mage specs (Tower Warden, harness, bots). */
export const MAGE_SPECS: SpecDef[] = [stormcallerSpec, rimecallerSpec, cinderbornSpec];

/** Preview info for all three (the launch set is bold in advancement.md). */
export const MAGE_SPEC_INFO: SpecInfo[] = [infoOf(stormcallerSpec, true), infoOf(rimecallerSpec, true), infoOf(cinderbornSpec, false)];
